import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import StatCounter from "@/components/home/primitives/StatCounter";
import { BUSINESS_NUMBERS } from "@/data/marketing/homepage";

export default function HomeNumbers() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();

  return (
    <section className="w-full bg-[#F9FAFC] py-16 sm:py-20 md:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Your first layer of support"
          title={
            <>
              Support that <GradientText>runs itself</GradientText>
            </>
          }
          subtitle="Help Genie sits in front of your team, resolving the everyday questions the moment they're asked, in your voice and from your own content. People get answers instantly, and your team only handles what genuinely needs them."
        />

        {/* Lifestyle banner */}
        <div className="mt-12 p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-xl">
          <div className="overflow-hidden rounded-[14px]">
            <img
              src="/home-page-imagery/feature-support.webp"
              alt="A customer getting instant answers from Help Genie at home"
              loading="lazy"
              className="h-56 sm:h-64 md:h-72 w-full object-cover object-center"
            />
          </div>
        </div>

        {/* Value rows — horizontal */}
        <div ref={ref} className="mt-8 space-y-4">
          {BUSINESS_NUMBERS.map((stat, i) => {
            const isPhrase =
              stat.animateTo === undefined && stat.display.length > 6;
            return (
              <div
                key={stat.id}
                className={cn(
                  "group flex flex-col gap-3 rounded-2xl border border-[#E5EAF0] bg-white px-6 py-5 md:px-8 md:py-6 shadow-sm transition-all duration-700 ease-out hover:-translate-y-0.5 hover:shadow-md sm:flex-row sm:items-center sm:gap-8",
                  inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                )}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="font-bold sm:w-56 sm:shrink-0">
                  <div
                    className={cn(
                      isPhrase
                        ? "text-2xl md:text-3xl leading-tight"
                        : "text-4xl md:text-5xl leading-none"
                    )}
                  >
                    <GradientText>
                      <StatCounter stat={stat} />
                    </GradientText>
                  </div>
                </div>
                <div className="hidden sm:block h-12 w-px shrink-0 bg-[#E5EAF0]" />
                <p className="flex-1 text-sm md:text-base leading-relaxed text-gray-600">
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-8 p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
          <div className="rounded-[14px] bg-white px-6 py-5 md:px-8 md:py-6 text-center">
            <p className="text-base md:text-lg text-gray-700">
              Help Genie becomes the{" "}
              <span className="font-semibold text-[#222B45]">
                first thing your customers and team reach for
              </span>
              : instant answers, in your voice, at a fraction of what a support
              team costs.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
